/**
 * VICTORY SCENE - Pantalla de Victoria
 * Mostrada cuando el jugador completa un ciclo
 */

import Phaser from "phaser";
import { SCENES, TEXTS, COLORS, MESSAGES } from "../utils/constants.js";
import { fillTemplate } from "../utils/helpers.js";

export default class VictoryScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENES.VICTORY });
  }

  init(data) {
    // Recibir datos de la ronda completada
    this.roundData = {
      integrity: data.integrity || 100,
      votesUsed: data.votesUsed || 0,
      totalVotes: data.totalVotes || 20,
      round: data.round || 1,
      nextCycle: data.nextCycle || "2030-2034",
    };
  }

  create() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // ========================================
    // BACKGROUND DE VICTORIA SEGÚN RONDA
    // ========================================
    const bgKey = `bg_win_round_${this.roundData.round}`;
    const background = this.add.image(width / 2, height / 2, bgKey);

    // Escalar para cubrir toda la pantalla
    const scaleX = width / background.width;
    const scaleY = height / background.height;
    const scale = Math.max(scaleX, scaleY);
    background.setScale(scale);
    background.setDepth(-1); // Detrás de todo

    console.log(`🎉 Background de victoria cargado: ${bgKey}`);

    // Overlay verde semi-transparente (opcional, para mantener sensación de éxito)
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x48bb78, // Verde de éxito
      0.3 // 30% de opacidad
    );
    overlay.setDepth(0);

    // Animación de celebración
    //this.createCelebrationEffect();

    // Título
    const title = this.add.text(
      width / 2,
      height * 0.2,
      MESSAGES.VICTORY.CYCLE_COMPLETE,
      {
        fontSize: "48px",
        fontWeight: "bold",
        color: "#ffffff",
        stroke: "#2d5016",
        strokeThickness: 6,
        fontFamily: "Arial",
      }
    );
    title.setOrigin(0.5);

    // Animación del título
    this.tweens.add({
      targets: title,
      scale: 1.1,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    // Estadísticas
    this.showStats(width / 2, height * 0.45);

    // Mensaje de siguiente ciclo
    const nextCycleText = fillTemplate(MESSAGES.VICTORY.NEXT_CYCLE, {
      nextCycle: this.roundData.nextCycle,
    });

    const nextCycle = this.add.text(width / 2, height * 0.65, nextCycleText, {
      fontSize: "24px",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    nextCycle.setOrigin(0.5);

    // Botón CONTINUAR
    this.createContinueButton(width / 2, height * 0.8);

    // Botón COMPARTIR
    this.createShareButton(width / 2, height * 0.9);

    console.log("🎉 Victoria - Ciclo completado");
  }

  /**
   * Crear efecto de celebración
   */
  createCelebrationEffect() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Partículas de celebración (simplificado)
    for (let i = 0; i < 20; i++) {
      const particle = this.add.circle(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height),
        Phaser.Math.Between(5, 15),
        0xffffff,
        0.8
      );

      this.tweens.add({
        targets: particle,
        alpha: 0,
        y: particle.y + Phaser.Math.Between(50, 200),
        duration: Phaser.Math.Between(1000, 2000),
        repeat: -1,
        delay: Phaser.Math.Between(0, 1000),
      });
    }
  }

  /**
   * Mostrar estadísticas de la ronda
   */
  showStats(x, y) {
    const stats = [
      `Integridad final: ${Math.round(this.roundData.integrity)}%`,
      `Votos usados: ${this.roundData.votesUsed}/${this.roundData.totalVotes}`,
      `Ciclo completado: ${this.roundData.round}`,
    ];

    stats.forEach((stat, index) => {
      const statText = this.add.text(x, y + index * 40, stat, {
        fontSize: "20px",
        color: "#ffffff",
        fontFamily: "Arial",
        backgroundColor: "#00000044",
        padding: { x: 20, y: 10 },
      });
      statText.setOrigin(0.5);
    });
  }

  /**
   * Crear botón CONTINUAR
   */
  createContinueButton(x, y) {
    const button = this.add.rectangle(x, y, 200, 60, 0xffffff);
    button.setStrokeStyle(4, 0x2d5016);
    button.setInteractive({ useHandCursor: true });

    const buttonText = this.add.text(x, y, TEXTS.CONTINUE, {
      fontSize: "28px",
      fontWeight: "bold",
      color: "#2d5016",
      fontFamily: "Arial",
    });
    buttonText.setOrigin(0.5);

    // Efectos hover
    button.on("pointerover", () => {
      button.setFillStyle(0xccffcc);
      this.tweens.add({
        targets: [button, buttonText],
        scale: 1.1,
        duration: 100,
      });
    });

    button.on("pointerout", () => {
      button.setFillStyle(0xffffff);
      this.tweens.add({
        targets: [button, buttonText],
        scale: 1.0,
        duration: 100,
      });
    });

    // Click - Siguiente ronda
    button.on("pointerdown", () => {
      this.continueToNextRound();
    });
  }

  /**
   * Crear botón COMPARTIR
   */
  createShareButton(x, y) {
    const buttonText = this.add.text(x, y, TEXTS.SHARE, {
      fontSize: "18px",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    buttonText.setOrigin(0.5);
    buttonText.setInteractive({ useHandCursor: true });

    buttonText.on("pointerover", () => {
      buttonText.setColor("#ccffcc");
    });

    buttonText.on("pointerout", () => {
      buttonText.setColor("#ffffff");
    });

    buttonText.on("pointerdown", () => {
      this.shareResults();
    });
  }

  /**
   * Continuar al siguiente ciclo
   */
  continueToNextRound() {
    const nextRound = this.roundData.round + 1;

    // Si completó las 4 rondas, ir a FinalVictoryScene
    if (this.roundData.round >= 4) {
      console.log("🏆 ¡VICTORIA TOTAL! - 4 ciclos completados");

      this.cameras.main.fade(300, 114, 187, 120);

      this.time.delayedCall(300, () => {
        this.scene.start(SCENES.FINAL_VICTORY, {
          integrity: this.roundData.integrity,
          totalVotesUsed: this.roundData.votesUsed,
          bestCycle: 1,
        });
      });

      return;
    }

    console.log("➡️ Siguiente ronda");

    // Limpiar localStorage para evitar conflictos
    localStorage.clear();

    this.cameras.main.fade(300, 0, 0, 0);

    this.time.delayedCall(300, () => {
      this.scene.start(SCENES.GAME, {
        round: nextRound,
        inheritedIntegrity: this.roundData.integrity,
      });

      console.log(
        `🎮 Iniciando Ronda ${nextRound} con ${this.roundData.integrity}% integridad`
      );
    });
  }

  /**
   * Compartir resultados
   */
  shareResults() {
    // TODO: Implementar sistema de compartir
    const shareText = `¡Mantuve la democracia viva por ${this.roundData.round} ciclos en Mala Hierba! 🏛️\n¿Puedes hacerlo mejor?\n\n[URL del juego]`;

    console.log("📤 Compartir:", shareText);

    // Por ahora, copiar al portapapeles
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareText);
      alert("¡Texto copiado al portapapeles!");
    } else {
      alert(shareText);
    }
  }
}
